import { useSetAtom } from 'jotai';
import { CircleAlert, X } from '@music-vine/cadence/icons';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useLocalStorage, useSessionStorage } from 'react-use';
import { topStickyBannerCountAtom, topStickyBannerHeightAtom } from '@/hooks/ui/useBannerVisibility';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@music-vine/cadence/ui';

/**
 * Action button configuration for the Banner component.
 * @note Only supported for `variant="float-bottom"`.
 */
export interface BannerAction {
  /** If provided, renders the button as a link */
  href?: string;
  /** Button text */
  label: string;
  /** Click handler */
  onClick?: () => void;
  /** If true, dismisses the banner after clicking */
  shouldDismiss?: boolean;
  /** Button size - defaults to 'sm' */
  size?: ButtonProps['size'];
  /** Button variant - defaults to 'bold' */
  variant?: ButtonProps['variant'];
}
interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Array of action buttons to display.
   * @note Only supported for `variant="float-bottom"`. Has no effect on `top-sticky` variant.
   */
  actions?: BannerAction[];
  children: React.ReactNode;
  closeButtonClassName?: string;
  condition?: boolean;
  currentPath?: string;
  excludePaths?: string[];
  id: string;
  onDismiss?: () => void;
  shouldShow?: (id: string) => boolean;
  showCloseButton?: boolean;
  showIcon?: boolean;
  type?: 'default' | 'alert' | 'warning' | 'uppbeat';
  useSessionStorageForDismiss?: boolean;
  variant?: 'top-sticky' | 'float-bottom';
}
export interface BannerRef {
  dismiss: () => void;
}
const defaultExcludePaths: string[] = [];
const BANNER_DISMISS_KEY_PREFIX = 'banner-dismissed-';

/**
 * Inline script for `<head>`: hides already-dismissed banners before first
 * paint. Banners are server rendered without access to the storage holding
 * their dismissal, so otherwise a dismissed banner paints for a moment until
 * hydration drops it (DEV-6495). Hydration then unmounts it for real, so this
 * only covers the pre-hydration window.
 */
export const dismissedBannersScript = `(function(){try{
var p=${JSON.stringify(BANNER_DISMISS_KEY_PREFIX)},stores=[localStorage,sessionStorage],s=[];
for(var i=0;i<stores.length;i++){for(var j=0;j<stores[i].length;j++){var k=stores[i].key(j);
if(k&&k.indexOf(p)===0&&stores[i].getItem(k)==='true'){var id=k.slice(p.length);
if(!/["\\\\]/.test(id)){s.push('[data-banner-id="'+id+'"]')}}}}
if(!s.length){return}
var el=document.createElement('style');el.textContent=s.join(',')+'{display:none!important}';
document.head.appendChild(el)}catch(e){}})()`;
const bannerVariants = {
  'top-sticky': {
    default: 'w-screen py-2 px-4 bg-gray-50 dark:bg-gray-800 flex items-center justify-center relative text-gray-950 dark:text-white',
    alert: 'bg-red-50 dark:bg-red-700 py-2 px-4 text-center text-red-950 dark:text-red-50 flex justify-between items-center border-b border-red-100 dark:border-red-600',
    warning: 'bg-yellow-50 dark:bg-yellow-500 py-2 px-4 text-center text-yellow-950 dark:text-yellow-950 flex justify-between items-center border-b border-yellow-950/10 dark:border-yellow-950',
    uppbeat: 'bg-pink-500 py-2 px-4 text-center text-white flex justify-between items-center border-b border-pink-500'
  },
  'float-bottom': {
    default: 'bg-white dark:bg-gray-900 px-container fl-p-3/4 text-center text-gray-950 dark:text-white flex justify-between items-center fixed bottom-4 inset-x-4 z-50 rounded-lg border-1 border-solid border-gray-150 dark:border-gray-800 flex-col sm:flex-row fl-gap-2/8 lg:max-w-fit mx-auto [.footer-player-active_&]:bottom-20',
    alert: 'bg-red-50 dark:bg-red-700 px-container fl-p-3/4 text-center text-red-950 dark:text-red-50 flex justify-between items-center fixed bottom-4 inset-x-4 z-50 rounded-lg border-1 border-solid border-red-100 dark:border-red-600 flex-col sm:flex-row fl-gap-2/8 lg:max-w-fit mx-auto [.footer-player-active_&]:bottom-20',
    warning: 'bg-yellow-50 dark:bg-yellow-500 px-container fl-p-3/4 text-center text-yellow-950 dark:text-yellow-950 flex justify-between items-center fixed bottom-4 inset-x-4 z-50 rounded-lg border-1 border-solid border-yellow-950/10 dark:border-yellow-950 flex-col sm:flex-row fl-gap-2/8 lg:max-w-fit mx-auto [.footer-player-active_&]:bottom-20',
    uppbeat: 'bg-pink-500 px-container fl-p-3/4 text-center text-white flex justify-between items-center fixed bottom-4 inset-x-4 z-50 rounded-lg border-1 border-solid border-pink-500 flex-col sm:flex-row fl-gap-2/8 lg:max-w-fit mx-auto [.footer-player-active_&]:bottom-20'
  }
};
const iconVariants = {
  default: null,
  alert: <CircleAlert className="size-5 shrink-0 text-red-800 dark:text-red-lighter" />,
  warning: <CircleAlert className="size-5 shrink-0 text-yellow-950 dark:text-yellow-950" />,
  uppbeat: null
};
export const Banner = forwardRef<BannerRef, BannerProps>(({
  id,
  children,
  className,
  closeButtonClassName,
  onDismiss,
  showCloseButton = true,
  showIcon = true,
  shouldShow,
  condition = true,
  excludePaths = defaultExcludePaths,
  currentPath = '',
  variant = 'top-sticky',
  type = 'default',
  useSessionStorageForDismiss = false,
  actions
}, ref) => {
  const [dismissed, setDismissed] = useLocalStorage<boolean>(`${BANNER_DISMISS_KEY_PREFIX}${id}`, false);
  const [sessionDismissed, setSessionDismissed] = useSessionStorage<boolean>(`${BANNER_DISMISS_KEY_PREFIX}${id}`, false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const setTopStickyBannerCount = useSetAtom(topStickyBannerCountAtom);
  const setTopStickyBannerHeight = useSetAtom(topStickyBannerHeightAtom);

  // Use session storage if specified, otherwise use local storage
  const isDismissed = useSessionStorageForDismiss ? sessionDismissed : dismissed;
  const setDismissedState = useSessionStorageForDismiss ? setSessionDismissed : setDismissed;

  // Check if current path is in exclude paths
  const isExcludedPath = useMemo(() => {
    if (!currentPath || excludePaths.length === 0) {
      return false;
    }
    return excludePaths.some(path => currentPath === path || path.endsWith('*') && currentPath.startsWith(path.slice(0, -1)));
  }, [currentPath, excludePaths]);
  const isVisible = useMemo(() => {
    if (isDismissed) {
      return false;
    }
    if (isExcludedPath) {
      return false;
    }
    if (!condition) {
      return false;
    }
    if (shouldShow) {
      return shouldShow(id);
    }
    return true;
  }, [isDismissed, id, shouldShow, condition, isExcludedPath]);

  // Update the global banner visibility state for top-sticky banners
  useEffect(() => {
    if (variant === 'top-sticky' && isVisible) {
      setTopStickyBannerCount(prev => prev + 1);

      // Measure and report banner height
      if (bannerRef.current) {
        const height = bannerRef.current.offsetHeight;
        setTopStickyBannerHeight(height);
      }

      // Cleanup: decrement counter when component unmounts or becomes invisible
      return () => {
        setTopStickyBannerCount(prev => Math.max(0, prev - 1));
        // Reset height when no banners are visible
        setTopStickyBannerHeight(prev => prev > 0 ? 0 : prev);
      };
    }
    return;
  }, [variant, isVisible, setTopStickyBannerCount, setTopStickyBannerHeight]);
  const handleDismiss = () => {
    setDismissedState(true);
    if (onDismiss) {
      onDismiss();
    }
  };
  useImperativeHandle(ref, () => ({
    dismiss: handleDismiss
  }));
  if (!isVisible) {
    return null;
  }
  const bannerClasses = cn(bannerVariants[variant][type], className);
  const icon = showIcon ? iconVariants[type] : null;

  // Get the appropriate close button styling based on variant and type
  const getCloseButtonClasses = () => {
    const baseClasses = 'absolute right-2 h-8 w-8 border-none hover:bg-black/20';
    if (type === 'uppbeat') {
      return cn(baseClasses, 'text-white hover:bg-white/20', closeButtonClassName);
    }
    if (type === 'warning') {
      return cn(baseClasses, 'text-yellow-700 dark:text-yellow-300', closeButtonClassName);
    }
    return cn(baseClasses, 'text-[#5A646F]', closeButtonClassName);
  };

  // Helper to render action buttons
  const renderActions = () => {
    if (!(actions?.length || showCloseButton)) {
      return null;
    }
    const defaultDismissVariant = (() => {
      if (type === 'uppbeat') {
        return 'contrast';
      }
      if (type === 'alert') {
        return 'error';
      }
      return 'bold';
    })();
    return <div className="flex w-full gap-2 sm:w-auto" data-sentry-component="renderActions" data-sentry-source-file="banner.tsx">
          {actions?.map(action => {
        const handleActionClick = () => {
          action.onClick?.();
          if (action.shouldDismiss) {
            handleDismiss();
          }
        };
        const buttonProps = {
          variant: action.variant ?? 'bold',
          size: action.size ?? 'sm',
          onClick: handleActionClick,
          className: 'w-full sm:w-auto'
        } as const;
        if (action.href) {
          return <Button key={action.label} {...buttonProps} asChild>
                  <Link href={action.href}>{action.label}</Link>
                </Button>;
        }
        return <Button key={action.label} {...buttonProps}>
                {action.label}
              </Button>;
      })}
          {showCloseButton && <Button className="w-full sm:w-auto" onClick={handleDismiss} ref={closeButtonRef} size="sm" variant={actions?.length ? 'subtle' : defaultDismissVariant}>
              Dismiss
            </Button>}
        </div>;
  };

  // For float-bottom variant, wrap in AnimatePresence and motion.div
  if (variant === 'float-bottom') {
    return <AnimatePresence>
          <motion.div animate={{
        opacity: 1,
        y: 0
      }} className={bannerClasses} data-banner-id={id} exit={{
        opacity: 0,
        y: 20
      }} initial={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.2
      }}>
            <div className="fl-gap-2/3 flex items-center">
              {icon}
              <div className="flex-1">
                <div className="fl-text-sm/base m-0 text-balance text-left font-sans">
                  {children}
                </div>
              </div>
            </div>
            {renderActions()}
          </motion.div>
        </AnimatePresence>;
  }

  // For top-sticky variant, use original layout
  return <div className={bannerClasses} data-banner-id={id} ref={bannerRef}>
        {/* QUESTION: Why add pr-6 sm:pr-0? It prevents the content from being centered on mobile screens */}
        <div className={cn('mx-auto flex w-full items-center justify-between sm:container sm:w-auto sm:justify-center', {
      'pr-6 sm:pr-0': showCloseButton
    })}>
          {icon && <div className="fl-gap-2/3 flex items-center pr-2">{icon}</div>}
          <div className="fl-text-sm/base m-0 w-full text-balance text-left font-sans">
            {children}
          </div>
        </div>

        {showCloseButton && <Button className={getCloseButtonClasses()} onClick={handleDismiss} ref={closeButtonRef} size="icon" variant="transparent">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>}
      </div>;
});
Banner.displayName = 'Banner';